// frontend/src/pages/ConfirmEmail.jsx
// User lands here after clicking the email confirmation link.
// On mount: wait 1s → check session → redirect to /pricing or show error.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useLanguage } from '../utils/i18n';
import LanguageToggle from '../components/LanguageToggle';
import Logo from '../components/Logo';
import { ArrowLeft } from 'lucide-react';

export default function ConfirmEmail() {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // 'loading' | 'error'

    useEffect(() => {
        async function checkSession() {
            // Small delay to give Supabase auth listener a beat to process URL hash
            setTimeout(async () => {
                const { data: { session } } = await supabase.auth.getSession();
                if (session) {
                    // Ensure business row exists before redirecting
                    try {
                        const { data: existingBiz } = await supabase
                            .from('businesses')
                            .select('id')
                            .eq('user_id', session.user.id)
                            .single();

                        if (!existingBiz) {
                            const bizName = session.user.user_metadata?.business_name || 'My Business';
                            const { error: insertErr } = await supabase.from('businesses').insert({
                                user_id: session.user.id,
                                name: bizName,
                                color: '#1a73e8'
                            });
                            if (insertErr) throw insertErr;
                        }
                    } catch (err) {
                        console.error('[ConfirmEmail] Error ensuring business exists:', err);
                    }

                    // Immediately redirect to /pricing
                    navigate('/pricing', { replace: true });
                } else {
                    setStatus('error');
                }
            }, 1000);
        }
        checkSession();
    }, [navigate]);

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', position: 'relative' }}>
            {/* Responsive Header */}
            <header className="header-container" style={{ position: 'relative', zIndex: 10 }}>
                <Link to="/" className="btn btn-outline glass-btn header-back-minimal" style={{ textDecoration: 'none' }}>
                    <ArrowLeft size={18} /> <span className="hide-mobile" style={{ marginLeft: 8 }}>{t('back_to_home')}</span>
                </Link>
                <div className="header-centered-logo">
                    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                        <Logo size={24} />
                        <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', letterSpacing: -0.5 }}>Wafiyo</span>
                    </Link>
                </div>
                <LanguageToggle />
            </header>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', position: 'relative' }}>
                <div className="bg-shapes hide-mobile">
                    <div className="shape shape-1" style={{ top: '-10%', left: '-10%', width: 600, height: 600, opacity: 0.15 }}></div>
                    <div className="shape shape-2" style={{ bottom: '-10%', right: '-10%', width: 800, height: 800, opacity: 0.1 }}></div>
                </div>

                <div className="glass-card" style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1, textAlign: 'center', padding: '32px 24px' }}>
                    {status === 'loading' && (
                        <div>
                            <div className="logo-spinner" style={{ margin: '0 auto 20px', borderTopColor: 'var(--primary)', borderRightColor: 'var(--primary)', width: 40, height: 40 }} />
                            <h2 style={{ fontSize: 20, color: 'var(--text)' }}>{t('verifying')}</h2>
                        </div>
                    )}

                    {status === 'error' && (
                        <div>
                            <div style={{ fontSize: 56, marginBottom: 16 }}>❌</div>
                            <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>{t('invalid_link')}</h1>
                            <p style={{ color: 'var(--text-muted)', fontSize: 15, marginBottom: 32, lineHeight: 1.5 }}>
                                {t('invalid_link_msg')}
                            </p>
                            <Link
                                to="/register"
                                className="btn btn-outline"
                                style={{ width: '100%', justifyContent: 'center', textDecoration: 'none' }}
                            >
                                {t('back_to_register') || 'Volver al registro'}
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            <footer className="landing-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <Logo size={28} />
                            <span className="logo-text">Wafiyo</span>
                            <p className="footer-tagline">
                                {t('footer_tagline')}
                            </p>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>© {new Date().getFullYear()} Wafiyo. {t('all_rights_reserved')}</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
